import React from 'react';
import {
  faChevronDown,
  faChevronUp,
  faGripDotsVertical,
} from '@fortawesome/pro-regular-svg-icons';
import { Button, Icon, Text } from '../../atoms';

interface Item {
  id: string;
  label: string;
  highlight?: boolean;
  order: number;
}

export interface Props {
  initialItems: Array<Item>;
}

const SortableList: React.FunctionComponent<Props> = ({ initialItems }) => {
  const [items, setItems] = React.useState<Array<Item>>(initialItems);
  const draggedItemIndex = React.useRef<number | null>(null);

  const handleDragStart = (index: number) => {
    draggedItemIndex.current = index;
  };

  const handleDragOver: React.DragEventHandler<HTMLLIElement> = (event) => {
    event.preventDefault();
  };

  const handleDrop = (index: number) => {
    const fromIndex = draggedItemIndex.current;
    if (fromIndex === null || fromIndex === index) return;

    const updatedItems = [...items];
    const [draggedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(index, 0, draggedItem);

    setItems(updatedItems);
    draggedItemIndex.current = null;
  };

  const moveItemUp = (index: number) => {
    if (index === 0) return; // skip if first item
    const updatedItems = [...items];
    const [item] = updatedItems.splice(index, 1);
    updatedItems.splice(index - 1, 0, item);
    setItems(updatedItems);
  };

  const moveItemDown = (index: number) => {
    if (index === items.length - 1) return; // skip if last item
    const updatedItems = [...items];
    const [item] = updatedItems.splice(index, 1);
    updatedItems.splice(index + 1, 0, item);
    setItems(updatedItems);
  };

  return (
    <ul className="d-flex flex-column py-2 rounded-md border border-1 border-grayDark">
      {items.map((item, index) => (
        <li
          key={item.id}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop(index)}
          className="d-flex gap-2 flex-items-center py-2 px-3"
        >
          <Text size="lg1" weight="bold" className="w-5">
            {index + 1}º
          </Text>
          <Button variant="link" size="sm" className="m-0 p-0">
            <Icon icon={faGripDotsVertical} size="md" />
          </Button>
          <Text
            className="flex-1"
            size="md"
            weight={item.highlight ? 'bold' : 'regular'}
          >
            {item.label}
          </Text>
          <Button
            variant="link"
            size="sm"
            className="m-0 p-0"
            onClick={() => moveItemUp(index)}
            disabled={index === 0}
          >
            <Icon
              icon={faChevronUp}
              size="md"
              color={index === 0 ? 'disabled' : undefined}
            />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="m-0 p-0"
            onClick={() => moveItemDown(index)}
            disabled={index === items.length - 1}
          >
            <Icon
              icon={faChevronDown}
              size="md"
              color={index === items.length - 1 ? 'disabled' : undefined}
            />
          </Button>
        </li>
      ))}
    </ul>
  );
};

export default SortableList;
